/* eslint-disable no-underscore-dangle */
import { useCallback, useMemo } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Option } from '@sorare/core/src/components/search/Option';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useVirtualToggle from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  FILTERS,
  FilterWidget,
  VIRTUAL_TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';

import useFacetCounts from 'hooks/search/useFacetCounts';

export const DEFAULT_VALUE = 'global:all_star';

const FILTER = VIRTUAL_TOGGLE_FILTERS.competitionEligibilityFilter;

const leagueSlugsByCompetition: Record<string, string[]> = {
  Champion: [
    'premier-league-gb-eng',
    'laliga-es',
    'bundesliga-de',
    'serie-a-it',
    'ligue-1-fr',
  ],
  Challenger: [
    'eredivisie',
    'mlspa',
    'jupiler-pro-league',
    'spor-toto-super-lig',
    'campeonato-brasileiro-serie-a',
    'superliga-dk',
    'primeira-liga-pt',
    'football-league-championship',
  ],
  Contender: [
    '1-hnl',
    'austrian-bundesliga',
    '2-bundesliga',
    'premiership-gb-sct',
    'k-league-1',
    'j1-league',
    'segunda-division-es',
    'liga-mx',
    'superliga-argentina-de-futbol',
    'serie-b-it',
    'primera-division-cl',
    'ligue-2-fr',
    'liga-pro',
    'primera-division-peru',
    'chinese-super-league',
    'super-league-ch',
    'russian-premier-league',
    'eliteserien',
  ],
};

const ActiveLeagues = styled.div`
  padding-left: var(--double-unit);
`;

const divider = css`
  position: relative;
  isolation: isolate;
  padding-top: var(--double-unit);
  &:before {
    content: '';
    position: absolute;
    top: var(--unit);
    left: 0;
    width: 100%;
    height: 1px;
    background-color: var(--c-nd-100);
  }
`;
const OptionWrapper = styled.div`
  &:has(.active-leagues) {
    ${divider}
  }
  &:has(.active-leagues) + .option-wrapper:not(:has(.active-leagues)) {
    ${divider}
  }
`;

const useCurrentLeaguesRefinements = ({
  competition,
  skip,
}: {
  competition: string | undefined;
  skip: boolean;
}) => {
  const { results } = useInstantSearch();
  const { language } = useIntlContext();
  const { filters } = useSearchCardsContext();
  const i18nLeagueAttribute = FILTERS.activeLeague.i18nAttribute(language);

  const filtersWithoutSearchCardsContextFilters =
    results._state.filters?.replace(` AND ${joinFiltersWithAnd(filters)}`, '');

  const params = useMemo(
    () => ({
      ...results._state,
      maxValuesPerFacet: 100,
      filters: filtersWithoutSearchCardsContextFilters,
      disjunctiveFacetsRefinements: {
        ...results._state.disjunctiveFacetsRefinements,
        [i18nLeagueAttribute]: [],
      },
      facetFilters: [
        ...(results._state.facetFilters || []),
        Object.values(leagueSlugsByCompetition)
          .flat()
          .map(slug => `active_competitions:${slug}`),
      ],
    }),
    // force lifecycle to run when competition or results change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results._state, competition]
  );

  const leagues = useFacetCounts({
    index: results.index,
    attribute: i18nLeagueAttribute,
    skip,
    params,
  });

  const { refine: refineLeague, items: refinedLeagues } = useRefinementList({
    attribute: i18nLeagueAttribute,
    limit: 100,
  });

  const clearRefinedLeagues = useCallback(() => {
    refinedLeagues.forEach(
      ({ value, isRefined }) => isRefined && refineLeague(value)
    );
  }, [refineLeague, refinedLeagues]);

  return {
    leagues,
    refineLeague,
    refinedLeagues: refinedLeagues
      .filter(({ isRefined }) => isRefined)
      .map(({ value }) => value),
    clearRefinedLeagues,
  };
};

const CompetitionEligibilityFilter = ({ visible }: { visible: boolean }) => {
  const { sport = Sport.FOOTBALL } = useSportContext();

  const {
    so5: { competitionEligibilityAlgoliaFilters },
  } = useConfigContext();

  const competitions = Object.entries(
    competitionEligibilityAlgoliaFilters
  ).filter(([k, v]) => v && k);

  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });
  const track = useEvents();

  const { refineLeague, refinedLeagues, leagues, clearRefinedLeagues } =
    useCurrentLeaguesRefinements({
      competition: currentRefinement,
      skip: !visible || sport !== Sport.FOOTBALL,
    });

  const refineAllLeaguesFromCompetitionExcept = useCallback(
    (competition: string, league: string) => {
      const leaguesToRefine = Object.keys(leagues).filter(
        l =>
          leagueSlugsByCompetition[competition].includes(l.split('|')[1]) &&
          l !== league
      );
      leaguesToRefine.forEach(l => refineLeague(l));
    },
    [leagues, refineLeague]
  );

  const onChange = useCallback(
    (league: string) => {
      const isActive = currentRefinement === league;
      const newValue = isActive ? undefined : league;

      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: league,
        });
      }
    },
    [currentRefinement, setVirtualToggle, track]
  );

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      {competitions.map(([competition]) => (
        <OptionWrapper className="option-wrapper" key={competition}>
          <Option
            label={competition}
            active={currentRefinement === competition}
            onClick={() => {
              onChange(competition);
              clearRefinedLeagues();
            }}
            variant="radio"
          />
          {leagueSlugsByCompetition[competition] &&
            Object.entries(leagues).length > 0 && (
              <ActiveLeagues className="active-leagues">
                {Object.entries(leagues).map(([league, count]) => {
                  if (
                    leagueSlugsByCompetition[competition].some(s =>
                      league.endsWith(`|${s}`)
                    )
                  ) {
                    const parentCompetitionIsRefined =
                      currentRefinement === competition;
                    const leagueRefined = refinedLeagues.includes(league);
                    const [label, slug] = league.split('|');
                    return (
                      <Option
                        before={
                          <LeagueAvatar
                            size={24}
                            league={{
                              name: label,
                              slug,
                            }}
                          />
                        }
                        key={league}
                        label={label}
                        count={count}
                        active={parentCompetitionIsRefined || leagueRefined}
                        onClick={() => {
                          if (parentCompetitionIsRefined) {
                            // this means all nested leagues are refined
                            // toggle off the parent competition
                            onChange(competition);
                            // toggle on all other leagues, while this one will be off
                            refineAllLeaguesFromCompetitionExcept(
                              competition,
                              league
                            );
                          } else {
                            // toggle off the competititon refinement if it's on
                            if (currentRefinement) onChange(currentRefinement);
                            if (!leagueRefined && competition) {
                              // this means the league will get refined so if all siblings are also refined,
                              // toggle the competition
                              const allSiblingsSlugs = leagueSlugsByCompetition[
                                competition
                              ].filter(s => s !== slug);
                              const siblingsRefined = allSiblingsSlugs
                                .map(s =>
                                  refinedLeagues.find(l => l.endsWith(`|${s}`))
                                )
                                .filter(Boolean);
                              if (
                                siblingsRefined.length ===
                                allSiblingsSlugs.length
                              ) {
                                // toggle on parent competition
                                onChange(competition);
                                // toggle off all leagues (they will still appear on)
                                siblingsRefined.forEach(l => refineLeague(l));
                                return;
                              }
                            }
                            // toggle the league
                            refineLeague(league);
                          }
                        }}
                        variant="checkbox"
                      />
                    );
                  }
                  return null;
                })}
              </ActiveLeagues>
            )}
        </OptionWrapper>
      ))}
    </FilterSection>
  );
};

const Title = () => {
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];

  return <FilterTitle name={FILTER.title!} selected={selected} />;
};

const widget: FilterWidget = {
  key: FILTER.name,
  type: 'virtualToggle',
  filter: FILTER,
  Component: CompetitionEligibilityFilter,
  title: <Title />,
};

export default widget;
